import { ACTIVITY_GROUPS, REPORT_WHO_MAX, REPORT_WHO_SEPARATOR, type ActivityGroup, type ActivityQuery } from "@gioia365/contracts";
import { dayParam, PERIOD_PARAMS, personParam } from "../conversations/conversation-search";
import { searchParamText } from "../leads/lead-list-hooks";

// L'indirizzo di «Attività» (SEZ-8, M7 parte 2): periodo, «Di chi», «Che cosa» e il Lead cercato stanno nell'indirizzo
// (`/attivita?periodo=ieri&chi=<id>,<id>&cosa=chiamate&q=rossi`), così un link o il ritorno dalla scheda di un Lead riaprono la stessa
// vista. I periodi sono quelli di SEZ-3, con le parole di «Conversazioni» e del Report; quello di partenza è «Oggi». I valori di
// partenza («Oggi», «Tutto il team», «Tutte le azioni», nessuna ricerca) non si scrivono; un valore sconosciuto vale come assente.
// «Personalizzato» porta i due giorni («Dal» e «Al», `AAAA-MM-GG`): senza tutti e due vale «Oggi»; al contrario si leggono in ordine.

/** I periodi di «Attività» con le parole dell'indirizzo e quelle del server; «Oggi» è quello di partenza. */
export const ACTIVITY_PERIOD_PARAMS = {
  oggi: PERIOD_PARAMS.oggi,
  ieri: PERIOD_PARAMS.ieri,
  settimana: PERIOD_PARAMS.settimana,
  mese: PERIOD_PARAMS.mese,
  personalizzato: PERIOD_PARAMS.personalizzato,
} as const satisfies Record<string, ActivityQuery["period"]>;
export type ActivityPeriodParam = keyof typeof ACTIVITY_PERIOD_PARAMS;

export const DEFAULT_ACTIVITY_PERIOD = "oggi" satisfies ActivityPeriodParam;

/** I periodi nell'ordine di SEZ-3, come i pulsanti. */
export const ACTIVITY_PERIOD_ORDER: readonly ActivityPeriodParam[] = ["oggi", "ieri", "settimana", "mese", "personalizzato"];

/** «Che cosa»: la parola dell'indirizzo di ogni gruppo di azioni. */
export const GROUP_PARAMS = {
  chiamate: "calls",
  esiti: "outcomes",
  note: "notes",
  whatsapp: "whatsapp",
  status: "status",
  owner: "owner",
  task: "tasks",
  appuntamenti: "appointments",
  opportunita: "opportunities",
  sequenza: "sequence",
  "dati-lead": "lead_data",
  importazioni: "imports",
  impostazioni: "settings",
  gioia365: "platform",
} as const satisfies Record<string, ActivityGroup>;
export type GroupParam = keyof typeof GROUP_PARAMS;

export type ActivitySearch = {
  periodo?: Exclude<ActivityPeriodParam, typeof DEFAULT_ACTIVITY_PERIOD>;
  /** «Dal» e «Al» di «Personalizzato» (`AAAA-MM-GG`, «Dal» mai dopo «Al»): ci sono soltanto con quel periodo, tutti e due. */
  dal?: string;
  al?: string;
  /** «Di chi»: le appartenenze scelte, separate da una virgola; senza, «Tutto il team». Vale soltanto per chi vede il team. */
  chi?: string;
  cosa?: GroupParam;
  q?: string;
};

const isPeriod = (value: unknown): value is ActivityPeriodParam => typeof value === "string" && Object.hasOwn(ACTIVITY_PERIOD_PARAMS, value);
const isGroup = (value: unknown): value is GroupParam => typeof value === "string" && Object.hasOwn(GROUP_PARAMS, value);

/** La parola dell'indirizzo di un gruppo del server. */
export function groupParamOf(group: ActivityGroup): GroupParam {
  const entry = Object.entries(GROUP_PARAMS).find(([, value]) => value === group);
  return (entry?.[0] ?? "chiamate") as GroupParam;
}

/** I parametri dell'indirizzo `/attivita`: un valore sconosciuto o di forma sbagliata vale come assente. */
export function validateActivitySearch(raw: Record<string, unknown>): ActivitySearch {
  const result: ActivitySearch = {};
  const word = raw.periodo;
  if (isPeriod(word) && word !== DEFAULT_ACTIVITY_PERIOD) {
    if (word !== "personalizzato") result.periodo = word;
    else {
      const dal = dayParam(raw.dal);
      const al = dayParam(raw.al);
      // Due giorni `AAAA-MM-GG` si confrontano come testi.
      if (dal !== undefined && al !== undefined) Object.assign(result, dal <= al ? { periodo: word, dal, al } : { periodo: word, dal: al, al: dal });
    }
  }
  const parts = typeof raw.chi === "string" ? raw.chi.split(REPORT_WHO_SEPARATOR) : [raw.chi];
  const chosen = new Set<string>();
  for (const part of parts) {
    const id = personParam(part);
    if (id !== undefined) chosen.add(id);
    if (chosen.size === REPORT_WHO_MAX) break;
  }
  if (chosen.size > 0) result.chi = [...chosen].join(REPORT_WHO_SEPARATOR);
  if (isGroup(raw.cosa)) result.cosa = raw.cosa;
  const q = searchParamText(raw.q);
  if (q !== undefined) result.q = q;
  return result;
}

/** Che cosa chiedere al server, senza cursore né limite: «Di chi» soltanto per chi vede le azioni del team. */
export type ActivityRequest = Omit<ActivityQuery, "cursor" | "limit">;

/** La richiesta per il server dall'indirizzo. */
export function activityQueryOf(search: ActivitySearch, byPerson: boolean): ActivityRequest {
  const period = ACTIVITY_PERIOD_PARAMS[search.periodo ?? DEFAULT_ACTIVITY_PERIOD];
  const query: ActivityRequest = period === "custom" && search.dal !== undefined && search.al !== undefined ? { period, from: search.dal, to: search.al } : { period: period === "custom" ? "today" : period };
  if (byPerson && search.chi) query.who = search.chi;
  if (search.cosa) query.group = GROUP_PARAMS[search.cosa];
  const q = search.q?.trim();
  if (q) query.q = q;
  return query;
}

/** Le persone scelte in «Di chi», nell'ordine dell'indirizzo; nessuna vuol dire «Tutto il team». */
export function chosenPeopleOf(search: ActivitySearch): string[] {
  return search.chi ? search.chi.split(REPORT_WHO_SEPARATOR) : [];
}

/** Tutti i gruppi, nell'ordine del filtro «Che cosa». */
export const GROUP_ORDER: readonly ActivityGroup[] = ACTIVITY_GROUPS;
